'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
// usePathname from next/navigation (NOT @/i18n/navigation): this gate renders
// deep in client trees (e.g. the tutorial view) where the next-intl client
// context isn't always established during SSR, which threw "No intl context
// found" at render. We only need the current path to build a same-origin
// post-sign-in return URL, and the plain hook needs no intl provider. It returns
// the path WITH any locale prefix, which is a valid relative `next` target (the
// auth page re-validates it as same-origin).
import { usePathname } from 'next/navigation'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface ContentGateProps {
  /** Whether the content is gated */
  isGated: boolean
  /** Whether the current user has access */
  hasAccess: boolean
  /** Whether the current user is logged in (changes CTA destination) */
  isAuthenticated?: boolean
  /** Excerpt shown to non-subscribers in place of the gated body */
  excerpt?: string
  /** Learning outcomes shown above the paywall CTA */
  learningOutcomes?: string[]
  /** Show the decorative fade-to-white strip above the paywall card.
   *  Defaults to `true`. Layouts where the gate doesn't sit directly
   *  under prose (e.g. the editorial /tutorials/[slug] view, where
   *  there's a clear visual break above) should pass `false`. */
  showFade?: boolean
  /** Show the lead-capture email form below the paywall card.
   *  Defaults to `true`. Layouts that already provide engagement
   *  options elsewhere (or that want a tighter gate) can pass
   *  `false`. Note: the form is additionally hidden for authenticated
   *  users regardless of this flag — we already have a signed-in
   *  user's email, so re-asking for it is redundant. */
  showEmailCapture?: boolean
  /** The full content — only rendered if user has access or content is not gated */
  children: React.ReactNode
}

/**
 * Content gate — wraps any content and shows a paywall if the user lacks access.
 *
 * If content is not gated OR user has access: renders children normally.
 * If content is gated AND user lacks access: shows preview excerpt +
 * learning outcomes + upgrade CTA + email capture form. The full content is
 * never sent to the client.
 */
export function ContentGate({
  isGated,
  hasAccess,
  isAuthenticated = false,
  excerpt,
  learningOutcomes,
  showFade = true,
  showEmailCapture = true,
  children,
}: ContentGateProps) {
  // Carry the current path so post-sign-in / post-register returns the user
  // here instead of home. usePathname() is locale-aware (next-intl); the auth
  // page validates `next` is a same-origin relative path before using it.
  const pathname = usePathname()
  const t = useTranslations('content')
  const next = pathname ? encodeURIComponent(pathname) : ''
  const signInHref = next ? `/auth?next=${next}` : '/auth'
  const registerHref = next ? `/auth?mode=register&next=${next}` : '/auth?mode=register'

  // Not gated or user has access — show full content
  if (!isGated || hasAccess) {
    return <>{children}</>
  }

  // Gated and no access — show paywall
  return (
    <div>
      {/* Excerpt shown above the paywall CTA */}
      {excerpt && (
        <div className="mb-6">
          <p className="text-gray-700 leading-relaxed">{excerpt}</p>
        </div>
      )}

      {/* Paywall */}
      <div className="relative">
        {/* Fade overlay — opt-out via `showFade={false}` for layouts
            that don't have prose-fading-into-gate above the gate. */}
        {showFade && (
          <div className="h-24 bg-gradient-to-b from-transparent to-white pointer-events-none" />
        )}

        {/* CTA card. `@container` so the CTA button row can respond to the
            CARD's width, not the viewport — the gate sits in a narrow center
            column when the reader's side rails are open, where viewport
            breakpoints would wrongly keep the buttons side-by-side and overflow. */}
        <div className="@container border-2 border-brand-200 bg-brand-50 p-8 text-center" data-testid="content-gate">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center bg-brand-100">
            <Lock className="h-6 w-6 text-brand-700" />
          </div>

          <h3 className="text-xl font-bold text-gray-900">
            {t('membersOnly')}
          </h3>

          <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
            {t('gateBody')}
          </p>

          {/* What You'll Learn — learning outcomes */}
          {learningOutcomes && learningOutcomes.length > 0 && (
            <div className="mt-4 text-left max-w-md mx-auto">
              <p className="text-sm font-semibold text-gray-700 mb-2">
                {t('whatYoullLearn')}
              </p>
              <ul className="space-y-1.5">
                {learningOutcomes.map((outcome, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="text-green-600 mt-0.5">&#10003;</span>
                    {outcome}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex flex-col items-center gap-3 @md:flex-row @md:justify-center">
            {isAuthenticated ? (
              <Link href="/account?tab=profile">
                <Button className="bg-brand-700 hover:bg-brand-800 text-white font-semibold px-8">
                  {t('subscribeInAccount')}
                </Button>
              </Link>
            ) : (
              <>
                <Link href={registerHref}>
                  <Button className="bg-brand-700 hover:bg-brand-800 text-white font-semibold px-8">
                    {t('becomeMember')}
                  </Button>
                </Link>
                <Link href={signInHref}>
                  <Button variant="outline" className="border-brand-300 text-brand-700 hover:bg-brand-50">
                    {t('alreadyMember')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Email Capture — opt-out via `showEmailCapture={false}`, and
            always hidden for authenticated users (we already have their
            email; the "get notified" ask is only useful for logged-out
            visitors). */}
        {showEmailCapture && !isAuthenticated && <EmailCaptureForm />}
      </div>
    </div>
  )
}

/**
 * Email capture form — collects emails for the "pre-member" list.
 * Shown below the paywall CTA on gated content.
 */
function EmailCaptureForm() {
  const t = useTranslations('content')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return

    setStatus('loading')
    try {
      const res = await fetch('/api/lead-capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
      const data = await res.json()
      if (res.ok) {
        setStatus('success')
        setMessage(t('notifySuccess'))
        setEmail('')
      } else {
        setStatus('error')
        setMessage(data.error || t('notifyError'))
      }
    } catch {
      setStatus('error')
      setMessage(t('notifyNetworkError'))
    }
  }

  return (
    <div className="mt-6 border border-gray-200 bg-white p-6 text-center max-w-md mx-auto">
      <p className="text-sm font-semibold text-gray-700 mb-1">
        {t('notifyHeading')}
      </p>
      <p className="text-xs text-gray-500 mb-3">
        {t('notifyBody')}
      </p>

      {status === 'success' ? (
        <p className="text-sm text-green-700 font-medium">{message}</p>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="email"
            required
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-500"
          />
          <Button
            type="submit"
            disabled={status === 'loading'}
            className="bg-brand-700 hover:bg-brand-800 text-white text-sm px-4"
          >
            {status === 'loading' ? t('notifySaving') : t('notifyButton')}
          </Button>
        </form>
      )}
      {status === 'error' && (
        <p className="mt-2 text-xs text-red-600">{message}</p>
      )}
    </div>
  )
}

/**
 * Lock badge — small indicator for content listings showing gated items.
 *
 * Brand-blue square box with a white lock icon. Same recipe everywhere
 * a member-locked item shows up — content grids, mega-menu thumbnails,
 * leaderboard rows. Square corners distinguish it from the rounded
 * engagement pills (Like / Bookmark) on the same cards.
 *
 * Two sizes:
 *   - `md` (default): used on full-size content cards / hero thumbs.
 *   - `sm`: tighter padding + smaller icon, for tiny side-card thumbs
 *     in the mega menu where the default would crowd the artwork.
 *
 * `label` is the localized accessible name. The card grids always pass one
 * from their single per-card translator, so the badge itself adds NO hook
 * there (200+ card grids — iOS call-stack caution). Callers without a
 * translator (mega menu) omit it and get the localized "Members Only" via a
 * small wrapper that owns the one hook. `className` is merged for callers
 * that need to position the badge (`absolute right-2 top-2`, etc.).
 */
export function GatedBadge({
  label,
  className = '',
  size = 'md',
}: {
  label?: string
  className?: string
  size?: 'sm' | 'md'
} = {}) {
  if (label === undefined) return <DefaultLabelGatedBadge className={className} size={size} />
  const badgeLabel = label
  const padding = size === 'sm' ? 'p-0.5' : 'p-1'
  const iconSize = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'
  return (
    <span
      role="img"
      aria-label={badgeLabel}
      title={badgeLabel}
      className={`inline-flex items-center justify-center bg-brand-700 ${padding} text-white shadow ${className}`}
    >
      <Lock className={iconSize} aria-hidden />
    </span>
  )
}

function DefaultLabelGatedBadge({ className, size }: { className: string; size: 'sm' | 'md' }) {
  const tFilter = useTranslations('filters')
  return <GatedBadge label={tFilter('membersOnly')} className={className} size={size} />
}
